import type { Camera } from '@/types';
import type { FrameScheduler } from '@/core/rendering/frameScheduler';
import { sampleTransition, transitionDuration, type CameraTransition } from '@/core/camera/cameraAnimation';
import { camerasEqual } from '@/core/camera/cameraOps';
import { useCameraStore } from '@/store/cameraStore';

/**
 * Drives smooth camera transitions through the frame scheduler. One transition at a
 * time; a new target replaces the current one from the camera's live position, and any
 * direct user input (pan, wheel, pinch) cancels it so the user is never fought.
 */
export class CameraAnimator {
  private current: CameraTransition | null = null;
  private reduceMotion = false;

  constructor(
    private readonly frames: FrameScheduler,
    private readonly now: () => number = () => performance.now(),
  ) {}

  setReduceMotion(value: boolean): void {
    this.reduceMotion = value;
  }

  get isAnimating(): boolean {
    return this.current !== null;
  }

  to(target: Camera, durationMs?: number): void {
    const store = useCameraStore.getState();
    const from = store.camera;
    if (camerasEqual(from, target)) {
      this.current = null;
      return;
    }
    const duration = this.reduceMotion
      ? 0
      : (durationMs ?? transitionDuration(from, target, store.viewport.size.width));
    if (duration === 0) {
      this.current = null;
      store.setCamera(target);
      return;
    }
    this.current = { from, to: target, startedAt: this.now(), duration };
    this.frames.schedule(this.tick);
  }

  cancel(): void {
    this.current = null;
  }

  private readonly tick = (): void => {
    const t = this.current;
    if (!t) return;
    const { camera, done } = sampleTransition(t, this.now());
    useCameraStore.getState().setCamera(camera);
    if (done) this.current = null;
    else this.frames.schedule(this.tick);
  };
}
